// NEW_FILE_JUSTIFIED: Extracted from App.tsx to fix no-giant-component diagnostic
import { useState } from 'react';
import { useAuthActions } from '@convex-dev/auth/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export function SignInPage() {
  const { signIn } = useAuthActions();
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setIsPending(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    try {
      await signIn('password', { email, password, flow: 'signIn' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('InvalidAccountId') || msg.includes('Could not find')) {
        setError("This account doesn't exist. Please sign up first.");
      } else if (
        msg.includes('InvalidSecret') ||
        msg.includes('InvalidPassword') ||
        msg.includes('incorrect')
      ) {
        setError('Incorrect password. Please try again.');
      } else if (msg.includes('already exists')) {
        setError('This account already exists. Please sign in instead.');
      } else {
        setError(msg || 'Sign in failed. Please try again.');
      }
    } finally {
      setIsPending(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="w-full max-w-md p-8 bg-card rounded-lg border border-border">
        <h1 className="text-3xl font-bold mb-2 text-foreground">Sign In</h1>
        <p className="text-foreground-muted mb-6">Welcome back to Lead Management</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email" className="text-foreground-muted">
              Email
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="you@example.com"
              required
              disabled={isPending}
              data-testid="signin-email"
              className="mt-1 bg-background-input border-border text-foreground placeholder:text-foreground-placeholder focus:border-border-hover"
            />
          </div>

          <div>
            <Label htmlFor="password" className="text-foreground-muted">
              Password
            </Label>
            <Input
              id="password"
              name="password"
              type="password"
              placeholder="••••••••"
              required
              disabled={isPending}
              data-testid="signin-password"
              className="mt-1 bg-background-input border-border text-foreground placeholder:text-foreground-placeholder focus:border-border-hover"
            />
          </div>

          {error && (
            <div
              className="p-3 text-sm text-red-400 bg-red-900/20 border border-red-900/50 rounded-md"
              role="alert"
            >
              {error}{' '}
              {error.includes('sign up') && (
                <a href="/signup" className="text-link hover:underline font-medium">
                  Sign up here
                </a>
              )}
            </div>
          )}

          <Button
            type="submit"
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
            disabled={isPending}
            data-testid="signin-submit"
          >
            {isPending ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        <p className="mt-4 text-center text-sm text-foreground-muted">
          Don't have an account?{' '}
          <a href="/signup" className="text-link hover:underline">
            Sign up
          </a>
        </p>
      </div>
    </div>
  );
}
